import React, {Component} from 'react'
import {GeoJSON, LayerGroup, Map, Marker, TileLayer} from 'react-leaflet'
import Leaflet from 'leaflet'
import L from 'leaflet'
import CircularProgress from '@material-ui/core/CircularProgress';
import 'leaflet/dist/leaflet.css'
import './DeliveryMap.scss';
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapLayersPanel from "./MapLayersPanel/MapLayersPanel";
import {renderMessengerMarker, renderOrderMarker} from "./Markers/renderFunctions";
import ReactDOMServer from "react-dom/server";
import _ from "lodash";
import {AppContext} from "../../Utils/AppContext";
import {eventsSubject, publish} from "../../../Events/events";
import {filter} from "rxjs/operators";
import {
    FocusOrder,
    FocusOrdersInMap,
    GroupHoverOnGroupsToolbar,
    GroupHoverOutOnGroupsToolbar,
    OrderClickedOnGrid,
    OrderClickedOnMap,
    OrderHoverOnGrid,
    OrderHoverOnMap,
    OrderHoverOutOnGrid,
    OrderHoverOutOnMap
} from "../../../Events/topics";
import MapAdditionalButtons from "./MapAdditionalButtons/MapAdditionalButtons";
import {getMessageByType} from "../../../Utils/MessageTypeToUI";
import PlacesOfDepartureImages from "../../../Utils/PlacesOfDepartureImages";
import {shallowEqualObjects} from "shallow-equal";
import "overlapping-marker-spiderfier-leaflet/dist/oms";
import {withResizeDetector} from 'react-resize-detector';


Leaflet.Icon.Default.imagePath =
    '//cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/';

const style = {};

mapboxgl.setRTLTextPlugin(
    "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.1/mapbox-gl-rtl-text.js"
);

class DeliveryMap extends Component {
    static contextType = AppContext;

    constructor(props) {
        super(props);

        const layersVisibilityRaw = localStorage.getItem('layersVisibility');
        let layersVisibility = {};

        if (layersVisibilityRaw)
            layersVisibility = JSON.parse(layersVisibilityRaw);

        this.state = {
            layers: this.setMapLayers(layersVisibility)
        };

        this.doubleClickData = undefined;
        this.doubleClickClearTimer = undefined;

        this.finishedLoading = false;

        this.blinkFocusOrderSubscription = eventsSubject
            .pipe(filter(f => f.topic === FocusOrder))
            .subscribe(m => this.blinkFocusOrderId(m.data.orderId));

        this.zoomToOrderSubscription = eventsSubject
            .pipe(filter(f => f.topic === OrderClickedOnGrid))
            .subscribe(m => this.zoomToOrderIds([m.data.orderId]));

        this.focusOrdersInMapSubscription = eventsSubject
            .pipe(filter(f => f.topic === GroupHoverOnGroupsToolbar))
            .subscribe(m => this.focusOrdersInMap(m.data.orderIds));

        this.focusOrderInMapSubscription = eventsSubject
            .pipe(filter(f => f.topic === OrderHoverOnGrid))
            .subscribe(m => this.focusOrdersInMap([m.data.orderId]));

        this.clearFocusOrdersInMapSubscription = eventsSubject
            .pipe(filter(f => f.topic === OrderHoverOutOnGrid || f.topic === GroupHoverOutOnGroupsToolbar))
            .subscribe(() => this.focusOrdersInMap(undefined));
    }

    componentDidMount() {
        this.mapApi = this.map.leafletElement;
        this.oms = new window.OverlappingMarkerSpiderfier(this.mapApi, {keepSpiderfied: true});

        window.addEventListener("animationstart", this.updateLeafletMap.bind(this));
        window.addEventListener("animationiteration", this.updateLeafletMap.bind(this));
        window.addEventListener("animationend", this.updateLeafletMap.bind(this));

        this.fetchUpdatedDeliveriesData(this.props.match.params.bid);
        setInterval(() => this.fetchUpdatedDeliveriesData(this.props.match.params.bid), 10000);
    }

    componentWillReceiveProps(nextProps) {
        const isDataFirstFetched = (
            (!this.props.unsentOrders && nextProps.unsentOrders) ||
            (!this.props.sentOrders && nextProps.sentOrders) ||
            (!this.props.business && nextProps.business));

        const hasSomeData = (nextProps.unsentOrders) || (nextProps.sentOrders) || (nextProps.business);

        if (isDataFirstFetched || (hasSomeData && !this.didFitBounds)) {
            this.fitBounds(1.5, nextProps.business, nextProps.sentOrders, nextProps.unsentOrders);

            if (!this.didFitBounds)
                this.didFitBounds = true;
        }

        const allOrders = [...nextProps.unsentOrders || [], ...nextProps.sentOrders || []];

        this.assignColorsForGroups(allOrders.flatMap(order => order.groupId ? [order.groupId] : []));

        if (nextProps.width !== this.width || nextProps.height !== this.height) {
            this.width = nextProps.width;
            this.height = nextProps.height;
        }
    }

    componentWillUnmount() {
        this.focusOrderInMapSubscription.unsubscribe();
        this.blinkFocusOrderSubscription.unsubscribe();
        this.clearFocusOrdersInMapSubscription.unsubscribe();
        this.focusOrdersInMapSubscription.unsubscribe();
        this.zoomToOrderSubscription.unsubscribe();

        window.removeEventListener("animationstart", this.updateLeafletMap.bind(this));
        window.removeEventListener("animationiteration", this.updateLeafletMap.bind(this));
        window.removeEventListener("animationend", this.updateLeafletMap.bind(this));
    }

    focusOrdersInMap(orderIds) {
        publish(FocusOrdersInMap, orderIds);
    }

    zoomToOrderIds(orderIds) {
        if (orderIds) {
            const allOrders = (this.props.unsentOrders || []).concat(this.props.sentOrders || []);

            const foundOrders = orderIds.map(orderId => allOrders.find(o => o.id === orderId)).filter(a => a);

            if (foundOrders)
                this.flyToLocations(foundOrders.map(order => order.location));
        }
    }

    blinkFocusOrderId(orderId) {
        if (!orderId) {
            if (this.focusOrderIdsTimeout)
                clearTimeout(this.focusOrderIdsTimeout);

            this.focusOrdersInMap();

            return;
        }

        const foundOrder = (this.props.unsentOrders || []).concat(this.props.sentOrders || []).find(o => o.id === orderId);

        if (!foundOrder)
            return;

        this.flyToLocation(foundOrder.location);

        this.focusOrdersInMap();

        this.focusOrderIdsTimeout = setTimeout(() => {
            this.focusOrdersInMap([orderId]);

            this.focusOrderIdsTimeout = setTimeout(() => {
                this.focusOrdersInMap();

                this.focusOrderIdsTimeout = setTimeout(() => {
                    this.focusOrdersInMap([orderId]);

                    this.focusOrderIdsTimeout = setTimeout(() => {
                        this.focusOrdersInMap();

                        this.focusOrderIdsTimeout = setTimeout(() => {
                            this.focusOrdersInMap([orderId]);

                            this.focusOrderIdsTimeout = null;
                        }, 400)
                    }, 400)
                }, 400)
            }, 400)
        }, 400)
    }

    updateLeafletMap() {
        this.mapApi.invalidateSize();
    }

    fetchUpdatedDeliveriesData(bid) {
        this.props.fetchDeliveryGroups(bid);
        this.props.fetchLockedDeliveryGroups(bid);
        this.props.fetchActiveMessengersLocations(bid);
    }

    assignColorsForGroups(groupsIds) {
        const uniqGroupsIds = _.uniq(groupsIds);
        const coloredGroupsIds = this.context.iconColors.getAssignedGroups();

        coloredGroupsIds.forEach(groupId => {
            if (!uniqGroupsIds.includes(groupId))
                this.context.iconColors.revokeGroupFromColor(groupId);
        });

        uniqGroupsIds.forEach(groupId => {
            this.context.iconColors.assignGroupToColor(groupId);
        });
    }

    setMapLayers(layersVisibility = {}) {
        return {
            accepted: {
                renderFunction: () => this.renderMarkersForOrders((this.props.unsentOrders || []).filter(o => o.status === "ACCEPTED")),
                visible: layersVisibility.accepted !== undefined ? layersVisibility.accepted : true,
                displayName: "הזמנות שהתקבלו"
            },
            ready: {
                renderFunction: () => this.renderMarkersForOrders((this.props.unsentOrders || []).filter(o => o.status === "READY")),
                visible: layersVisibility.ready !== undefined ? layersVisibility.ready : true,
                displayName: "הזמנות מוכנות"
            },
            scheduledReady: {
                renderFunction: () => this.renderMarkersForOrders((this.props.unsentOrders || []).filter(o => o.status === "SCHEDULED_READY")),
                visible: layersVisibility.scheduledReady !== undefined ? layersVisibility.scheduledReady : true,
                displayName: "הזמנות מתוזמנות"
            },
            assignedScheduledReady: {
                renderFunction: () => this.renderMarkersForSentOrders((this.props.sentOrders || []).filter(o => ["ASSIGNED_SCHEDULED_READY"].includes(o.status))),
                visible: layersVisibility.assigned !== undefined ? layersVisibility.assigned : true,
                displayName: "הזמנות מתוזמנות שמחכות לאיסוף"
            },
            assigned: {
                renderFunction: () => this.renderMarkersForSentOrders((this.props.sentOrders || []).filter(o => ["ASSIGNED", "COLLECTED"].includes(o.status))),
                visible: layersVisibility.assigned !== undefined ? layersVisibility.assigned : true,
                displayName: "הזמנות שבדרך ללקוח"
            },
            delivered: {
                renderFunction: () => this.renderMarkersForSentOrders((this.props.sentOrders || []).filter(o => o.status === "DELIVERED")),
                visible: layersVisibility.delivered !== undefined ? layersVisibility.delivered : true,
                displayName: "הזמנות פעילות שנמסרו"
            },
            activeMessengers: {
                renderFunction: () => this.renderActiveMessengers(this.props.activeMessengers || []),
                visible: layersVisibility.activeMessengers !== undefined ? layersVisibility.activeMessengers : true,
                displayName: "שליחים"
            }
        }
    }

    onLayerVisibleChange(layerId, value) {
        const layers = {...this.state.layers};
        layers[layerId].visible = value;

        this.setState({layers});

        const newLayersVisibility = Object.keys(layers).reduce(function (result, item) {
            result[item] = layers[item].visible;
            return result;
        }, {});

        localStorage.setItem("layersVisibility", JSON.stringify(newLayersVisibility));
    }

    handleMarkerMouseOver(orderId) {
        publish(OrderHoverOnMap, {orderId: orderId});
    }

    handleMarkerMouseOut(orderId) {
        publish(OrderHoverOutOnMap, {orderId: orderId});
    }

    handleMarkerClick(orderId) {
        if (this.doubleClickData && this.doubleClickData.orderId === orderId) {
            if (this.props.onMarkerDoubleClick)
                this.props.onMarkerDoubleClick(orderId);

            clearTimeout(this.doubleClickClearTimer);
            this.doubleClickData = undefined;
            this.doubleClickClearTimer = undefined;
        } else {
            if (this.doubleClickClearTimer)
                clearTimeout(this.doubleClickClearTimer);

            this.doubleClickData = {
                orderId: orderId
            };

            this.doubleClickClearTimer = setTimeout(() => {
                this.doubleClickData = undefined;
                this.doubleClickClearTimer = undefined;

                publish(OrderClickedOnMap, {orderId: orderId});
            }, 300);
        }
    }

    renderActiveMessengers(activeMessengers) {
        const markers = [];

        activeMessengers.forEach((messenger) => {
            if (!messenger.location || !messenger.location.lat || !messenger.location.lng)
                return;
            const groupId = this.getDeliveryIdOfUid(messenger.uid);

            markers.push(renderMessengerMarker(
                messenger,
                this.context.iconColors.getMessengersIconByGroupId(groupId),
                this.props.users,
                this.props.assignedDeliveryGroups)
            )
        });

        return markers;
    }

    renderMarkersForSentOrders(orders) {
        return (orders || []).map(order => renderOrderMarker(
            this.context.iconColors,
            order,
            this.handleMarkerClick.bind(this, order.id),
            this.handleMarkerMouseOver.bind(this, order.id),
            this.handleMarkerMouseOut.bind(this, order.id),
            order.status === 'DELIVERED' ? "#48ab54" : "#64b5f6",
            this.props.proposedDeliveryGroups,
            this.props.assignedDeliveryGroups,
            this.props.evaluatingProposedDeliveryGroups,
            this.props.readySla,
            this.props.placesOfDeparture));
    }

    renderMarkersForOrders(orders) {
        return (orders || []).map(order => renderOrderMarker(
            this.context.iconColors,
            order,
            this.handleMarkerClick.bind(this, order.id),
            this.handleMarkerMouseOver.bind(this, order.id),
            this.handleMarkerMouseOut.bind(this, order.id),
            '#f6f1e5',
            this.props.proposedDeliveryGroups,
            this.props.assignedDeliveryGroups,
            this.props.evaluatingProposedDeliveryGroups,
            this.props.readySla,
            this.props.placesOfDeparture));
    }

    renderBusinesses() {
        if (this.props.business.placesOfDeparture) {
            const isAllLocationsEqual = this.props.business.placesOfDeparture.slice(1).every(p => p.location.lat === this.props.business.location.lat && p.location.lng === this.props.business.location.lng);

            if (!isAllLocationsEqual) {
                const businessesMarkers = [];

                businessesMarkers.push(this.renderBusinessMarker(this.props.business.location, this.props.business.placesOfDeparture[0].name));

                for (let placeOfDeparture of this.props.business.placesOfDeparture.slice(1)) {
                    businessesMarkers.push(this.renderBusinessMarker(placeOfDeparture.location, placeOfDeparture.name));
                }

                return businessesMarkers;
            }
        }

        return this.renderBusinessMarker(this.props.business.location);
    }

    renderBusinessMarker(businessLocation, placeOfDepartureName) {
        const divIconRender = (
            <div className="business-marker">
                <div>
                    {this.context.iconColors.getHomeSvgWithColor()}
                </div>
                {
                    placeOfDepartureName ? (() => {
                            const placeOfDepartureImagePngBytes = PlacesOfDepartureImages.getImage(placeOfDepartureName);

                            if (placeOfDepartureImagePngBytes)
                                return (
                                    <div className="business-marker-place-of-departure">
                                        <img className="business-marker-place-of-departure-image"
                                             src={`data:image/png;base64,${placeOfDepartureImagePngBytes}`}/>
                                    </div>
                                );

                            return (
                                <div className="business-marker-place-of-departure">
                                    {placeOfDepartureName}
                                </div>
                            );
                        })()
                        :
                        null
                }
            </div>
        );

        const divIcon = Leaflet.divIcon({
            html: ReactDOMServer.renderToString(divIconRender),
            iconAnchor: new Leaflet.Point(16, 20),
            popupAnchor: new Leaflet.Point(0, -15),
            iconSize: null,
        });

        return <Marker position={businessLocation}
                       icon={divIcon}/>
    }

    getDeliveryIdOfUid(uid) {
        const group = (this.props.assignedDeliveryGroups || []).find((group) => {
            return group.messenger_id === uid
        });

        return group ? group.id : undefined;
    }

    flyToLocation(location, zoom = 16, duration = 1) {
        this.mapApi.flyTo([location.lat, location.lng], zoom,
            {
                animate: true,
                duration: duration
            });
    }

    flyToLocations(locations, maxZoom = 16, duration = 1) {
        if (locations.length === 0)
            return;

        if (locations.length === 1) {
            this.flyToLocation(locations[0], maxZoom, duration);
            return;
        }

        const minLat = Math.min.apply(Math, locations.map(location => location.lat));
        const maxLat = Math.max.apply(Math, locations.map(location => location.lat));
        const minLng = Math.min.apply(Math, locations.map(location => location.lng));
        const maxLng = Math.max.apply(Math, locations.map(location => location.lng));

        const dLat = maxLat - minLat;
        const dLng = maxLng - minLng;

        const fitBoundsTo = [
            [minLat - dLat * 0.11, minLng - dLng * 0.11],
            [maxLat + dLat * 0.11, maxLng + dLng * 0.11]
        ];

        const originalMaxZoom = this.mapApi.options.maxZoom;
        this.mapApi.setMaxZoom(maxZoom);

        this.mapApi.flyToBounds(fitBoundsTo,
            {
                animate: true,
                duration: duration
            });

        this.mapApi.setMaxZoom(originalMaxZoom);
    }

    fitBounds(duration, business, sentOrders, unsentOrders) {
        const locations = [];

        if (business)
            locations.push(business.location);

        if (sentOrders)
            locations.push(...sentOrders.map(o => o.location));

        if (unsentOrders)
            locations.push(...unsentOrders.map(o => o.location));

        if (locations.length > 0)
            this.flyToLocations(locations, 16, duration);
    }

    onFitBoundsClick() {
        this.fitBounds(1, this.props.business, this.props.sentOrders, this.props.unsentOrders);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !shallowEqualObjects({
            isLoading: this.props.isLoading,
            deliveryGroupsError: this.props.deliveryGroupsError,
            activeMessengers: this.props.activeMessengers,
            unsentOrders: this.props.unsentOrders,
            sentOrders: this.props.sentOrders,
            layers: this.state.layers,
            proposedDeliveryGroups: this.props.proposedDeliveryGroups,
            assignedDeliveryGroups: this.props.assignedDeliveryGroups,
            evaluatingProposedDeliveryGroups: this.props.evaluatingProposedDeliveryGroups,
            readySla: this.props.readySla,
            users: this.props.users
        }, {
            isLoading: nextProps.isLoading,
            deliveryGroupsError: nextProps.deliveryGroupsError,
            activeMessengers: nextProps.activeMessengers,
            unsentOrders: nextProps.unsentOrders,
            sentOrders: nextProps.sentOrders,
            layers: nextState.layers,
            proposedDeliveryGroups: nextProps.proposedDeliveryGroups,
            assignedDeliveryGroups: nextProps.assignedDeliveryGroups,
            evaluatingProposedDeliveryGroups: nextProps.evaluatingProposedDeliveryGroups,
            readySla: nextProps.readySla,
            users: nextProps.users
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.map && this.map.container)
            this.map.container.classList.remove('leaflet-touch');

        if (this.spiderfyMarkersLayerGroup && this.oms) {
            this.oms.clearMarkers();
            this.spiderfyMarkersLayerGroup.leafletElement.eachLayer(layer => {
                if (layer instanceof L.Marker) {
                    this.oms.addMarker(layer);
                }
            });
        }
    }

    render() {
        const startCenter = [32.077575, 34.778159];
        const startZoom = 2;
        const maxZoom = 21;

        let polygonToShow = null;

        if (this.props.polygonToShow) {
            try {
                polygonToShow = JSON.parse(this.props.polygonToShow);
            } catch {
            }
        }

        return (
            <div className="delivery-map">
                {
                    this.props.deliveryGroupsError && !this.props.isAutopilot ?
                        <div className="map-center-message-wrapper">
                            <div className="map-center-message-content">
                                <div className="map-center-message-text">
                                    {getMessageByType(this.props.deliveryGroupsError.type)}
                                </div>
                            </div>
                        </div>
                        :
                        false
                }
                <MapAdditionalButtons onFitBoundsClick={this.onFitBoundsClick.bind(this)}/>
                <MapLayersPanel layers={this.state.layers} onLayerVisibleChange={this.onLayerVisibleChange.bind(this)}/>
                <Map className="map-container"
                     ref={(ref) => {
                         this.map = ref;
                         window.map = ref;
                     }}
                     tap={false}
                     attributionControl={false}
                     style={style}
                     center={startCenter}
                     maxZoom={maxZoom}
                     doubleClickZoom={false}
                     zoom={startZoom}
                >
                    <TileLayer
                        url={"http://{s}.google.com/vt/lyrs=m&hl=he&x={x}&y={y}&z={z}&apistyle=s.t%3A2|s.e%3Al|p.v%3Aoff"}
                        subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                    />
                    {this.renderBusinesses()}
                    {this.state.layers.activeMessengers.visible ? this.state.layers.activeMessengers.renderFunction() : null}
                    <LayerGroup
                        ref={(ref) => {
                            this.spiderfyMarkersLayerGroup = ref;
                        }}>
                        {this.state.layers.accepted.visible ? this.state.layers.accepted.renderFunction() : null}
                        {this.state.layers.scheduledReady.visible ? this.state.layers.scheduledReady.renderFunction() : null}
                        {this.state.layers.assignedScheduledReady.visible ? this.state.layers.assignedScheduledReady.renderFunction() : null}
                        {this.state.layers.ready.visible ? this.state.layers.ready.renderFunction() : null}
                        {this.state.layers.assigned.visible ? this.state.layers.assigned.renderFunction() : null}
                        {this.state.layers.delivered.visible ? this.state.layers.delivered.renderFunction() : null}
                    </LayerGroup>
                    {
                        polygonToShow &&
                        <GeoJSON
                            data={polygonToShow}
                            fillColor={"rgba(0,66,255,0.22)"}
                        />
                    }

                </Map>
            </div>
        )
    }
}

export default withResizeDetector(DeliveryMap);
